<script setup>

import { ref, useTemplateRef, onMounted } from 'vue';
import ShareModel from '../models/ShareModel.js';
import MainModel from '../models/MainModel.js';
import EmptyState from './EmptyState.vue';
import { copyToClipboard, getResultFolderPath, getResultLocationLabel } from '../utils.js';
import { Button, Icon, InputDialog, ProgressBar, TableView, useNotify } from '@cloudron/pankow';
import { prettyDate, prettyLongDate } from '@cloudron/pankow/utils';
import moment from 'moment';

const emit = defineEmits(['item-activated']);

const { notify } = useNotify();

const tableColumns = {
  icon: {
    label: '',
    width: '35px',
    sort: false
  },
  target: {
    label: 'Path',
    sort: true
  },
  receiver: {
    label: 'With',
    sort: true
  },
  expiresAt: {
    label: 'Expires at',
    sort: (a, b) => {
      if (!a && !b) return 0;
      if (!a) return 1;
      if (!b) return -1;
      return moment(a).isBefore(moment(b)) ? -1 : 1;
    },
  },
  createdAt: {
    label: 'Created at',
    sort: (a, b) => moment(a).isBefore(moment(b)) ? 1 : -1,
  },
  action: {
    label: '',
    sort: false
  }
};

const sharesInputDialog = useTemplateRef('sharesInputDialog');

const tableModel = ref([]);
const busy = ref(true);

async function refresh() {
  busy.value = true;

  const [shares, groups] = await Promise.all([ ShareModel.list(), MainModel.getGroups() ]);
  const groupNameById = {};
  groups.forEach((g) => { groupNameById[g.id] = g.name; });

  shares.forEach((share) => {
    share.file.href = `#/files/home${share.file.filePath}`;
    share.target = share.file.filePath.toLowerCase();
    if (share.receiverGroup) share.receiver = groupNameById[share.receiverGroup] || share.receiverGroup;
    else if (share.receiverUsername) share.receiver = share.receiverUsername;
    else share.receiver = share.name || share.receiverEmail || '';
  });

  tableModel.value = shares;
  busy.value = false;
}

function isLinkShare(share) {
  return !share.receiverUsername && !share.receiverGroup;
}

function onCopyLink(share) {
  copyToClipboard(ShareModel.getLink(share.id));
  notify('Share link copied to clipboard');
}

function onOpenName(share) {
  emit('item-activated', share.file);
}

async function onUnshare(share) {
  const yes = await sharesInputDialog.value.confirm({
    message: `Unshare "${share.file.fileName}"?`,
    confirmStyle: 'danger',
    confirmLabel: 'Unshare',
    rejectLabel: 'Cancel',
    rejectStyle: 'secondary'
  });

  if (!yes) return;

  try {
    await ShareModel.remove(share.id);
  } catch (e) {
    return console.error('Failed to unshare.', e);
  }

  await refresh();
}

onMounted(refresh);

</script>

<template>
  <div class="shares">
    <InputDialog ref="sharesInputDialog" />

    <ProgressBar v-if="busy" mode="indeterminate" :show-label="false" :slim="true" :show-track="false"/>
    <div class="shares-body" :class="{ 'has-shares': tableModel.length }" v-else>
      <EmptyState v-if="tableModel.length === 0" icon="fa-solid fa-share-from-square" title="Nothing shared by you" description="Files and folders you share will show up here" />
      <template v-else>
        <TableView :columns="tableColumns" :model="tableModel" default-sort-by="target">
          <template #icon="{ item:slotProps }"><img :src="slotProps.file.previewUrl" width="32" height="32" style="object-fit: cover;" /></template>
          <template #target="{ item:slotProps }">
            <div class="share-file">
              <a class="share-file-name" :href="slotProps.file.href" @click.prevent="onOpenName(slotProps)">{{ slotProps.file.fileName }}</a>
              <div class="share-file-path">
                <span class="share-file-location">{{ getResultLocationLabel(slotProps.file) }}</span>
                <span>{{ getResultFolderPath(slotProps.file) }}</span>
              </div>
            </div>
          </template>
          <template #receiver="{ item:slotProps }">
            <span class="share-with">
              <Icon icon="fa-regular fa-user" v-show="slotProps.receiverUsername"/>
              <Icon icon="fa-solid fa-user-group" v-show="slotProps.receiverGroup"/>
              {{ slotProps.receiver }}
              <Button v-if="isLinkShare(slotProps)" plain tool icon="fa-regular fa-copy" title="Copy link" class="share-copy" @click="onCopyLink(slotProps)"/>
              <Icon icon="fa-solid fa-lock" v-show="slotProps.passwordProtected" v-tooltip.top="'Password protected'"/>
            </span>
          </template>
          <template #expiresAt="{ item:slotProps }">
            <span v-if="slotProps.expiresAt" v-tooltip.top="prettyLongDate(slotProps.expiresAt)">{{ prettyDate(slotProps.expiresAt) }}</span>
            <span v-else>Never</span>
          </template>
          <template #createdAt="{ item:slotProps }"><span v-tooltip.top="prettyLongDate(slotProps.createdAt)">{{ prettyDate(slotProps.createdAt) }}</span></template>
          <template #action="{ item:slotProps }">
            <Button danger outline small @click="onUnshare(slotProps)">Unshare</Button>
          </template>
        </TableView>
      </template>
    </div>
  </div>
</template>

<style scoped>

.shares {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
  flex-grow: 1;
  min-height: 0;
}

.shares-body {
  padding: 0 20px;
  overflow: auto;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.shares-body.has-shares {
  justify-content: flex-start;
}

.share-file {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.share-file-name {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}

.share-file-name:hover {
  text-decoration: underline;
}

.share-file-path {
  display: flex;
  gap: 8px;
  font-size: 12px;
  min-width: 0;
}

.share-file-location {
  flex-shrink: 0;
  color: var(--pankow-color-text-secondary);
}

.share-with {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.share-copy {
  padding: 0;
  min-width: 0;
  border: 0;
  background: transparent;
  color: inherit;
  line-height: 1;
}

</style>
